import axios from "axios";
import { getToken } from "../auth/token";

const API_BASE = process.env.REACT_APP_API_URL || "";

export async function fetchUnreadCount() {
    const { data } = await axios.get(`${API_BASE}/api/messages/unread`, { withCredentials: true });
    return Number(data?.count || 0);
}

export async function fetchDialogs() {
    const { data } = await axios.get(`${API_BASE}/api/messages/dialogs`, { withCredentials: true });
    return Array.isArray(data) ? data : [];
}

export async function getOrCreateDialogWithUser(userId) {
    const { data } = await axios.post(`${API_BASE}/api/messages/with-user/${userId}`, {}, { withCredentials: true });
    return data;
}

export async function fetchDialogMessages(conversationId, beforeId = null, limit = 30) {
    const params = { limit };
    if (beforeId) {
        params.beforeId = beforeId;
    }
    const { data } = await axios.get(`${API_BASE}/api/messages/dialogs/${conversationId}/messages`, {
        params,
        withCredentials: true,
    });
    return data;
}

export async function sendPrivateMessage(recipientId, text) {
    const { data } = await axios.post(
        `${API_BASE}/api/messages/send`,
        { recipientId, text },
        { withCredentials: true },
    );
    return data?.message;
}

export async function markDialogAsRead(conversationId) {
    const { data } = await axios.post(`${API_BASE}/api/messages/dialogs/${conversationId}/read`, {}, { withCredentials: true });
    return data;
}

function getMessagesWebSocketUrl() {
    const explicit = (process.env.REACT_APP_MESSAGES_WS_URL || "").trim();
    if (explicit) {
        return explicit.replace(/\/$/, "");
    }
    const api = API_BASE.replace(/\/$/, "");
    if (api.startsWith("https://")) {
        const u = new URL(api);
        const port = process.env.REACT_APP_MESSAGES_WS_PORT || "2346";
        return `wss://${u.hostname}:${port}`;
    }
    if (api.startsWith("http://")) {
        const u = new URL(api);
        const port = process.env.REACT_APP_MESSAGES_WS_PORT || "2346";
        return `ws://${u.hostname}:${port}`;
    }
    if (typeof window !== "undefined") {
        const port = process.env.REACT_APP_MESSAGES_WS_PORT || "2346";
        const wsProto = window.location.protocol === "https:" ? "wss:" : "ws:";
        return `${wsProto}//${window.location.hostname}:${port}`;
    }
    return "ws://127.0.0.1:2346";
}

/**
 * @returns {{ close: () => void } | null}
 */
export function createMessagesWebSocket(onUnread, onNewMessage) {
    const token = getToken();
    if (!token) {
        return null;
    }

    const url = getMessagesWebSocketUrl();
    let ws = null;
    let reconnectTimer = null;
    let closed = false;

    const clearReconnect = () => {
        if (reconnectTimer !== null) {
            window.clearTimeout(reconnectTimer);
            reconnectTimer = null;
        }
    };

    const connect = () => {
        if (closed || !getToken()) {
            return;
        }
        const currentToken = getToken();
        if (!currentToken) {
            return;
        }
        try {
            ws = new WebSocket(url);
        } catch {
            scheduleReconnect();
            return;
        }

        ws.onopen = () => {
            ws.send(JSON.stringify({ type: "auth", token: currentToken }));
        };

        ws.onmessage = (event) => {
            let payload;
            try {
                payload = JSON.parse(event.data || "{}");
            } catch {
                return;
            }
            if (payload.type === "unread_messages_updated") {
                onUnread(Number(payload.count || 0));
            }
            if (payload.type === "new_private_message" && payload.message && onNewMessage) {
                onNewMessage(payload.message);
            }
        };

        ws.onerror = () => {};

        ws.onclose = () => {
            if (!closed) {
                scheduleReconnect();
            }
        };
    };

    const scheduleReconnect = () => {
        clearReconnect();
        reconnectTimer = window.setTimeout(() => {
            reconnectTimer = null;
            connect();
        }, 2500);
    };

    connect();

    return {
        close() {
            closed = true;
            clearReconnect();
            if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
                ws.close();
            }
            ws = null;
        },
    };
}
